import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { FaGoogleDrive, FaMicrosoft, FaDropbox } from 'react-icons/fa'
import { Heading, Box, Stack, ResponsiveStack, Alert, Text, Icon, Button } from '@/components/lib'
import utils from '../../../utils/utils'
import api from '../../../api'
import useStore from '../../../store'

import ConfirmButton from '../../includes/ConfirmButton'

// A connection that has failed to sync reports why, so the person who set it
// up can act on it without waiting for the email
function ConnectionStatus ({ connection }) {
  if (!connection.lastSyncError) {
    return <Alert status='success' withIcon>Trialflare is connected to this service.</Alert>
  }
  return (
    <Alert status='warning' withIcon title='The last sync to this service failed'>
      <Stack gap={1}>
        <Text>{connection.lastSyncError}{connection.lastSyncErrorAt ? ` (${utils.formatDateTime(connection.lastSyncErrorAt)})` : ''}</Text>
        <Text>Trialflare will try again when data is next submitted, and disconnects the service after three failed attempts in a row. If it keeps failing, disconnect and reconnect the service.</Text>
      </Stack>
    </Alert>
  )
}

function ResultsIntegrations () {
  const [connectedServices, setConnectedServices] = useState([])
  const location = useLocation()
  const { user, trial, team } = useStore()
  // Which service we are in the middle of connecting. Coming back from the
  // provider's consent screen, setting up the file and its first sync takes a
  // few seconds — without this the page looks like the connection never
  // happened. Seeded from the navigation state so the very first paint shows it.
  const [connecting, setConnecting] = useState(location.state?.code ? location.state.service : null)
  // Until the connections have loaded we don't know which services are already
  // set up, so the buttons shouldn't invite a connection that already exists
  const [loading, setLoading] = useState(!location.state?.code)
  // Where the OAuth round-trip should land: this tab, rather than whichever
  // Results tab happened to be showing when the connection was started
  const returnTo = `/trials/${trial?._id}/results/integrations`

  const loadConnectedServices = () => {
    setLoading(true)
    api.trials.getConnectedServices(trial._id, data => {
      setConnectedServices(data.connectedServices)
      setLoading(false)
    }, () => setLoading(false))
  }

  // Only the service being connected says so; the others are just unknown until
  // the list arrives
  const connectButtonProps = name => ({
    loading: loading || connecting === name,
    loadingText: connecting === name ? 'Connecting' : undefined
  })

  // Handle trial connection to an OAuth service. If code present in navigation, assume trying to connect
  useEffect(() => {
    const { state } = location // This is passed from navigate() in OAuthConnect component
    if (state?.code) {
      setConnecting(state.service)
      setLoading(false)
      api.trials.connectToService(trial._id, state.service, state.scope, state.code, data => {
        setConnectedServices(data.connectedServices)
        setConnecting(null)
        utils.toast('success', 'Connection success', 'Trialflare has successfully connected to the service.')
      }, err => {
        setConnecting(null)
        utils.toast('error', 'Unable to connect trial', err.message)
        loadConnectedServices()
      })
    } else {
      loadConnectedServices()
    }
  }, [location])

  // Leaving for the provider's consent screen is itself a redirect, so show the
  // button working from the moment it is clicked
  const startConnect = (name, connect) => {
    setConnecting(name)
    connect()
  }

  const disconnectService = name => {
    api.trials.disconnectFromService(trial._id, name, data => {
      setConnectedServices(data.connectedServices)
    }, err => utils.toast('Unable to connect trial', err.message))
  }

  const googleConnection = connectedServices.filter(s => s.name === 'google')[0]
  const microsoftConnection = connectedServices.filter(s => s.name === 'microsoft')[0]
  const dropboxConnection = connectedServices.filter(s => s.name === 'dropbox')[0]

  return (
    <Stack gap={5}>
      <Stack gap={3} borderWidth='1px' p={3} rounded='md' bg='bg.canvas'>
        <Heading as='h3' size='md'><Icon as={FaGoogleDrive} /> Google Drive</Heading>
        <Text>When you connect your Google Drive account, Trialflare will create a Google Sheet for this trial and sync data through to it in real-time.</Text>
        {googleConnection
          ? (
            <>
              <ConnectionStatus connection={googleConnection} />
              <Heading size='sm'>This trial is connected to a Google Sheet in {googleConnection.connectedUser?.firstName} {googleConnection.connectedUser?.lastName}'s account</Heading>

              <ResponsiveStack>
                <Button colorPalette='primary' as='a' href={`https://docs.google.com/spreadsheets/d/${googleConnection.fileId}`} target='_blank' rel='noopener noreferrer'><Icon as={FaGoogleDrive} /> Open in Google Sheets</Button>

                <ConfirmButton
                  trigger={<Button variant='outline' colorPalette='grey'>Disconnect from Drive</Button>}
                  header='Really disconnect from Google Drive?'
                  footer={'The trial\'s Sheet will remain in Drive until you choose to delete it.'}
                  onConfirm={e => disconnectService('google')}
                  actionColor='red'
                />
              </ResponsiveStack>
            </>)
          : (
              utils.canManageTrial(user, trial)
                ? (
                  <Box>
                    {utils.hasFeature(team, 'integrations')
                      ? <Button colorPalette='primary' {...connectButtonProps('google')} onClick={() => startConnect('google', () => utils.connectToGoogle('https://www.googleapis.com/auth/drive.file', returnTo))}>Connect to Google Drive</Button>
                      : <Alert variant='left-accent'>Your team plan does not include this type of integration.</Alert>}
                  </Box>)
                : <Alert status='warning' variant='left-accent'>Your account does not have permission to set up this integration.</Alert>)}
      </Stack>

      <Stack gap={3} borderWidth='1px' p={3} rounded='md' bg='bg.canvas'>
        <Heading as='h3' size='md'><Icon as={FaMicrosoft} /> Microsoft Office365</Heading>
        <Text>When you connect your Microsoft Office account, Trialflare will create an Excel file in OneDrive for this trial and sync data through to it in real-time.</Text>
        {microsoftConnection
          ? (
            <>
              <ConnectionStatus connection={microsoftConnection} />
              <Heading size='sm'>This trial is connected to an Excel workbook in {microsoftConnection.connectedUser?.firstName} {microsoftConnection.connectedUser?.lastName}'s account</Heading>

              <ResponsiveStack>
                <Button colorPalette='primary' as='a' href={microsoftConnection.url} target='_blank' rel='noopener noreferrer'><Icon as={FaMicrosoft} /> Open in Excel in Office365</Button>
                <ConfirmButton
                  trigger={<Button variant='outline' colorPalette='grey'>Disconnect from Office365</Button>}
                  header='Really disconnect from Office365?'
                  footer={'The trial\'s Excel spreadsheet will remain in OneDrive until you choose to delete it.'}
                  onConfirm={e => disconnectService('microsoft')}
                  actionColor='red'
                />
              </ResponsiveStack>
            </>)
          : (utils.canManageTrial(user, trial)
              ? (
                <Box>
                  {utils.hasFeature(team, 'integrations')
                    ? <Button colorPalette='primary' {...connectButtonProps('microsoft')} onClick={() => startConnect('microsoft', () => utils.connectToMicrosoft('offline_access Files.ReadWrite', returnTo))}>Connect to Office365</Button>
                    : <Alert variant='left-accent'>Your team plan does not include this type of integration.</Alert>}
                </Box>)
              : <Alert status='warning' variant='left-accent'>Your account does not have permission to set up this integration.</Alert>)}
      </Stack>

      <Stack gap={3} borderWidth='1px' p={3} rounded='md' bg='bg.canvas'>
        <Heading as='h3' size='md'><Icon as={FaDropbox} /> Dropbox</Heading>
        <Text>When you connect your Dropbox account, Trialflare will create an Excel file in Dropbox for this trial and sync data through to it in real-time.</Text>
        {dropboxConnection
          ? (
            <>
              <ConnectionStatus connection={dropboxConnection} />
              <Heading size='sm'>This trial is connected to a CSV file in {dropboxConnection.connectedUser?.firstName} {dropboxConnection.connectedUser?.lastName}'s Dropbox</Heading>

              <ResponsiveStack>
                <Button colorPalette='primary' as='a' href={dropboxConnection.url} target='_blank' rel='noopener noreferrer'><Icon as={FaDropbox} /> Open Trialflare folder in Dropbox</Button>
                <ConfirmButton
                  trigger={<Button variant='outline' colorPalette='grey'>Disconnect from Dropbox</Button>}
                  header='Really disconnect from Dropbox?'
                  footer={'The trial\'s data file will remain in Dropbox until you choose to delete it.'}
                  onConfirm={e => disconnectService('dropbox')}
                  actionColor='red'
                />
              </ResponsiveStack>
            </>)
          : (utils.canManageTrial(user, trial)
              ? (
                <Box>
                  {utils.hasFeature(team, 'integrations')
                    ? <Button colorPalette='primary' {...connectButtonProps('dropbox')} onClick={() => startConnect('dropbox', () => utils.connectToDropbox('files.content.write', returnTo))}>Connect to Dropbox</Button>
                    : <Alert variant='left-accent'>Your team plan does not include this type of integration.</Alert>}
                </Box>)
              : <Alert status='warning' variant='left-accent'>Your account does not have permission to set up this integration.</Alert>)}
      </Stack>
    </Stack>
  )
}

export default ResultsIntegrations
